import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Paper, Tooltip } from '@mui/material';
import { DataGrid, GridActionsColDef, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { UserListItem } from '../../../models/user/userListItem';
import { ListParams } from '../../../models/listParams';
import { getDefaultRoleFromLegacyFlag, getRoleLabel, OrganisatieUserRole } from '../../../models/user/userRole';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';
import OrganisatieUsersActionMenu from './OrganisatieUsersActionMenu';

import { userManagementService } from '../../../services/user-management.service';


interface OverzichtUsersProps {
  organisatieId: string;
}

const OrganisatieUsers: React.FC<OverzichtUsersProps> = ({ organisatieId }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserListItem[]>([]);
  
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!organisatieId) {
      console.error('Organisatie id is null');
      return;
    }

    setLoading(true);
    try {
      const params: ListParams = {
        offset: pageIndex * pageSize, 
        limit: pageSize, 
      };

      const data = await userManagementService.getUsersOrganisatie(organisatieId, params);
      const mappedUsers: UserListItem[] = (data.items ?? []).map((item: UserListItem) => {
        const userType = item.userType ?? getDefaultRoleFromLegacyFlag(item.keyUser);
        return {
          ...item,
          userType,
          keyUser: userType === 'keyUser',
        };
      });

      setUsers(mappedUsers);
      setTotalCount(data.count);
      setLoading(false);
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  }, [organisatieId, pageIndex, pageSize, navigate]);

  const gridColumns = useMemo<(GridColDef<UserListItem> | GridActionsColDef<UserListItem>)[]>(() => [
    {
      field: 'icon',
      headerName: 'Status',
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<UserListItem>) => {
        const isVerified = params.row.confirmed;
  
        return isVerified ? (
          <div className="status-icon">
            <Tooltip title="Account is actief. Deze gebruiker heeft zijn of haar e-mailadres geactiveerd via de activatie email." placement="right" arrow>
              <Icon color="success" style={{ fontSize: 24 }}>verified_user</Icon>
            </Tooltip>
          </div>
        ) : (
          <div className="status-icon">
            <Tooltip title="Account niet actief. Deze gebruiker heeft zijn of haar e-mailadres niet geactiveerd via de activatie email." placement="right" arrow>
              <Icon color="disabled" style={{ fontSize: 24 }}>remove_moderator</Icon>
            </Tooltip>
          </div>
        );
      },
    },
    { field: 'email', headerName: 'E-mail', flex: 3 },
    { 
      field: 'userType', 
      headerName: 'Type', 
      flex: 2,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<UserListItem>) => {
        const role = (params.row.userType ?? getDefaultRoleFromLegacyFlag(params.row.keyUser)) as OrganisatieUserRole;
        return <span>{getRoleLabel(role)}</span>;
      },
    },
    {
      field: 'actions',
      headerName: 'Acties',
      renderHeader: () => <></>,
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<UserListItem>) => (
        <OrganisatieUsersActionMenu params={params} organisatieId={organisatieId} onUserUpdate={fetchUsers} />
      ),
    },
  ], [fetchUsers, organisatieId]); // Dependencies

  
  useEffect(() => { setReady(true); }, []);

  useEffect(() => {
    if (!ready) return;
    fetchUsers();
  }, [ready, fetchUsers]); // Empty dependency array ensures this runs once when the component mounts

  const onChangePagination = async ({ page, pageSize }: { page: number, pageSize: number }) => {
    setPageIndex(page);
    setPageSize(pageSize);
  };

  return (
    <>
      <div className="grid-responsive">
        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={users}
            columns={gridColumns}
            getRowId={(row) => row.email || ''}
            disableColumnFilter
            disableColumnSorting 
            disableRowSelectionOnClick
            rowCount={totalCount}
            paginationMode="server"
            pageSizeOptions={[5, 10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: {
                  page: pageIndex,
                  pageSize: pageSize
                }
              }
            }}
            onPaginationModelChange={onChangePagination}
            sx={{ border: 0 }}
          />
        </Paper>
      </div>

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

    </>
  );
};

export default OrganisatieUsers;
